import React from 'react';
import { Check, Circle, Lock } from 'lucide-react';

export interface Task {
    id: string;
    label: string;
    minutes: number;
    module?: string;
    completed?: boolean;
    progress?: number; // 0-100
    deepLink?: string;
}

interface MissionTaskCardProps {
    task: Task;
    index: number;
    status: 'completed' | 'in-progress' | 'not-started';
    onClick?: () => void;
    isLocked?: boolean;
    isBonus?: boolean;
}

export const MissionTaskCard: React.FC<MissionTaskCardProps> = ({ task, index, status, onClick, isLocked, isBonus }) => {
    const isCompleted = status === 'completed';
    const isInProgress = status === 'in-progress';

    // Subject tag based on module or default
    const subjectTag = task.module || 'ZSV';

    return (
        <div
            onClick={!isLocked ? onClick : undefined}
            className={`
                relative p-4 rounded-xl border transition-all cursor-pointer group overflow-hidden
                ${isCompleted
                    ? 'bg-secondary/20 border-border/30 opacity-75'
                    : 'bg-card border-border hover:border-brand-orange/30'
                }
                ${isLocked ? 'cursor-not-allowed opacity-60' : ''}
            `}
        >
            {isLocked && (
                <div className="absolute inset-0 bg-muted/60 backdrop-blur-[1px] flex items-center justify-center z-20">
                    <div className="bg-muted/80 p-2 rounded-full border border-border">
                        <Lock className="w-5 h-5 text-muted-foreground" />
                    </div>
                </div>
            )}

            <div className={`flex items-start gap-4 ${isLocked ? 'blur-[0.5px]' : ''}`}>
                {/* Status Icon/Number */}
                <div className={`
                    w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold border
                    ${isCompleted
                        ? 'bg-green-500/20 text-green-500 border-green-500/30'
                        : isInProgress
                            ? 'bg-brand-orange text-white border-brand-orange'
                            : 'bg-secondary border-border text-muted-foreground'
                    }
                `}>
                    {isCompleted ? <Check className="w-4 h-4" /> : index + 1}
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-1">
                        <h4 className={`font-medium truncate pr-2 ${isCompleted ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                            {task.label}
                        </h4>
                        <span className="text-xs text-muted-foreground whitespace-nowrap pt-1">
                            {task.minutes} MIN
                        </span>
                    </div>

                    <div className="flex items-center gap-2 mb-2">
                        {isBonus && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-yellow-500/10 text-yellow-500 uppercase border border-yellow-500/20">
                                Bonus
                            </span>
                        )}
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-brand-orange/10 text-brand-orange uppercase">
                            {subjectTag}
                        </span>
                    </div>

                    {/* Progress Bar for In-Progress State */}
                    {isInProgress && (
                        <div className="mt-3 space-y-1">
                            <div className="flex justify-between text-xs text-muted-foreground">
                                <span>Probíhá</span>
                                <span>{task.progress || 0}%</span>
                            </div>
                            <div className="h-2 w-full bg-secondary rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-brand-orange to-orange-400 rounded-full transition-all duration-500"
                                    style={{ width: `${task.progress || 0}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Status Text for other states */}
                    {!isInProgress && (
                        <div className="mt-2 text-xs font-medium">
                            {isCompleted ? (
                                <span className="text-green-500">✓ Dokončeno</span>
                            ) : (
                                <span className="text-muted-foreground flex items-center gap-1">
                                    <Circle className="w-3 h-3" /> Nezačato
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
